import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { createWishlistShare } from "@/lib/wishlist.functions";
import { shareOrCopy } from "@/lib/share";
import { toast } from "sonner";
import { ProductCard } from "@/components/product-card";
import { fetchProducts } from "@/lib/queries";
import { Reveal } from "@/components/reveal";
import { useWishlist } from "@/hooks/use-wishlist";
import { useAuth } from "@/hooks/use-auth";
import { ProductSkeleton } from "@/components/skeleton-card";
import { Heart, Share2, Loader2 } from "lucide-react";

export const Route = createFileRoute("/wishlist")({
  head: () => ({
    meta: [
      { title: "Your Wishlist — Pluggz" },
      { name: "description", content: "Save and revisit the pieces you love from across Pluggz creator storefronts." },
      { property: "og:title", content: "Your Wishlist — Pluggz" },
      { property: "og:description", content: "Save and revisit the pieces you love from across Pluggz creator storefronts." },
      { property: "og:url", content: `${SITE_URL}/wishlist` },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/wishlist` }],
  }),
  component: Wishlist,
});

function Wishlist() {
  const { user, loading: authLoading } = useAuth();
  const { ids, loading } = useWishlist();
  const { data: products = [], isLoading: productsLoading } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });

  const saved = products.filter((p) => ids.includes(p.id));
  const showSkeletons = !!user && (loading || (ids.length > 0 && productsLoading));

  const createShare = useServerFn(createWishlistShare);
  const shareMut = useMutation({
    mutationFn: () => createShare(),
    onSuccess: async ({ token }) => {
      const url = `${SITE_URL}/wishlist/share/${token}`;
      await shareOrCopy({ title: "My Wishlist", url });
      toast.success("Wishlist link copied — share it with anyone");
    },
    onError: () => {
      toast.error("Could not create share link. Please try again.");
    },
  });

  return (
    <div className="mx-auto max-w-7xl px-6 pt-12 pb-24">
      <Reveal>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="editorial-eyebrow">Your vault</p>
            <h1 className="mt-2 font-serif text-5xl">
              <span className="italic shimmer">Wishlist</span>
            </h1>
            <p className="mt-3 text-muted-foreground">
              {!user ? "Sign in to start saving pieces." : `${saved.length} ${saved.length === 1 ? "piece" : "pieces"} saved.`}
            </p>
          </div>
          {user && saved.length > 0 && (
            <button
              type="button"
              onClick={() => shareMut.mutate()}
              disabled={shareMut.isPending}
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-muted transition disabled:opacity-60"
            >
              {shareMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              Share wishlist
            </button>
          )}
        </div>
      </Reveal>

      {!authLoading && !user && (
        <div className="mt-12 border border-border bg-card/40 px-6 py-12 text-center">
          <p className="text-sm text-muted-foreground">Your wishlist syncs across devices once you sign in.</p>
          <Link to="/login" search={{ redirect: "/wishlist" }} className="mt-4 inline-block border-b border-foreground pb-1 text-sm">Sign in</Link>
        </div>
      )}

      {user && !loading && !productsLoading && saved.length === 0 && (
        <Reveal>
          <div className="mx-auto mt-20 max-w-md text-center">
            <Heart className="mx-auto h-10 w-10 text-muted-foreground" />
            <h2 className="mt-6 font-serif text-4xl">Your wishlist is empty</h2>
            <p className="mt-4 text-sm text-muted-foreground">
              Browse creator storefronts and save the pieces you love.
            </p>
            <Link
              to="/discover"
              className="mt-8 inline-flex items-center border border-foreground/80 px-5 py-2.5 text-xs uppercase tracking-widest text-foreground transition-colors hover:bg-foreground hover:text-background"
            >
              Start discovering
            </Link>
          </div>
        </Reveal>
      )}

      {showSkeletons && (
        <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      )}

      {!showSkeletons && saved.length > 0 && (
        <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
          {saved.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 90}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
